import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { AGE_GROUPS, CLASSIFICATIONS, teamAttrs } from "@alpha/shared";
import { api, apiErrorMessage } from "../../../lib/api";
import { useAdminMutation, useAdminRegistrations, useTeamSearch } from "../../../lib/adminData";
import { teamAttrsById, teamName } from "../../../lib/bundle";
import { cx } from "../../../lib/cx";
import { entryRemoveConfirm } from "../../../lib/confirmCopy";
import { searchRows, type SearchRow } from "../../../lib/searchRows";
import type { Bundle, Division, Entry, Pool } from "../../../lib/types";
import { useDebouncedValue } from "../../../lib/useDebouncedValue";
import { Badge, Button, Input, SectionCard, Select } from "../../../components/ui";
import { orderedDivisions } from "./Divisions";
import type { TournamentContext } from "./TournamentShell";

/**
 * Who is in the event (console spec §5.3). The queue leads when something is
 * pending — that is the day's work before the event — and follows the entries
 * after. Add a team sits under the entries it adds to.
 */
export function Teams() {
  const { tid, bundle, pendingRegistrations } = useOutletContext<TournamentContext>();
  const queueFirst = (pendingRegistrations ?? 0) > 0;
  return (
    <div className="space-y-6">
      {queueFirst ? <Registrations tid={tid} /> : null}
      <Entries tid={tid} bundle={bundle} />
      <AddTeam tid={tid} bundle={bundle} />
      {queueFirst ? null : <Registrations tid={tid} />}
    </div>
  );
}

/**
 * Entries by division, and within a division by pool: one column per pool
 * plus Unassigned, each headed with its count, so an unbalanced or empty pool
 * is visible at a glance. A division over its cap says so in amber.
 */
function Entries({ tid, bundle }: { tid: string; bundle: Bundle }) {
  const divisions = orderedDivisions(bundle.divisions);
  return (
    <SectionCard
      title="Entries"
      badge={bundle.entries.length > 0 ? <Badge>{bundle.entries.length}</Badge> : undefined}
    >
      {divisions.length === 0 ? (
        <p className="text-sm text-slate-400">No divisions yet — add one under Setup.</p>
      ) : null}
      {divisions.map((d) => (
        <DivisionEntries key={d.id} tid={tid} bundle={bundle} division={d} />
      ))}
    </SectionCard>
  );
}

function DivisionEntries({ tid, bundle, division: d }: { tid: string; bundle: Bundle; division: Division }) {
  const pools = [...bundle.pools.filter((p) => p.divisionId === d.id)].sort((a, b) => a.order - b.order);
  const entries = bundle.entries.filter((e) => e.divisionId === d.id);
  const over = d.maxTeams !== undefined && entries.length > d.maxTeams;
  const columns: { key: string; title: string; pool: Pool | null; entries: Entry[] }[] =
    pools.length === 0
      ? [{ key: "all", title: "Teams", pool: null, entries }]
      : [
          ...pools.map((p) => ({ key: p.id, title: p.name, pool: p, entries: entries.filter((e) => e.poolId === p.id) })),
          { key: "none", title: "Unassigned", pool: null, entries: entries.filter((e) => e.poolId === null) },
        ];

  return (
    <div>
      <h3 className="mb-2 flex items-baseline gap-2 font-display font-semibold text-navy-950">
        {d.name}
        <span className={cx("text-sm font-medium tabular-nums", over ? "text-amber-700" : "text-slate-500")}>
          · {entries.length}
          {d.maxTeams !== undefined ? ` / ${d.maxTeams}` : ""}
        </span>
      </h3>
      {pools.length === 0 ? (
        <p className="mb-2 text-xs text-slate-400">Add pools under Setup to split them.</p>
      ) : null}
      <div className="grid gap-3 lg:grid-cols-[repeat(auto-fit,minmax(15rem,1fr))]">
        {columns.map((col) => (
          <div key={col.key} className="rounded-xl border border-slate-200 bg-slate-50/60 p-3">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
              {col.title} · {col.entries.length}
            </p>
            {col.entries.length === 0 ? (
              <p className="text-xs text-slate-400">Nobody here yet.</p>
            ) : (
              <div className="space-y-2">
                {col.entries.map((e) => (
                  <EntryCard key={e.id} tid={tid} bundle={bundle} entry={e} pools={pools} />
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * One entry: the team, the pool it plays in, and a two-step Remove whose
 * second step says what removing does — a registration-born entry goes back
 * to the queue, and a team on unsettled games stays named on them.
 */
function EntryCard({ tid, bundle, entry: e, pools }: { tid: string; bundle: Bundle; entry: Entry; pools: Pool[] }) {
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string>();
  const refresh = useAdminMutation(tid, () => Promise.resolve());
  const name = teamName(bundle, e.teamId);
  const attrs = teamAttrsById(bundle, e.teamId);
  const scheduledGames = bundle.games.filter(
    (g) => g.status === "scheduled" && (g.homeTeamId === e.teamId || g.awayTeamId === e.teamId),
  ).length;

  const run = (p: Promise<unknown>, fallback: string) =>
    p
      .then(() => {
        setError(undefined);
        refresh.mutate();
      })
      .catch((err) => setError(apiErrorMessage(err, fallback)));

  const remove = () => {
    if (!confirming) {
      setConfirming(true);
      return;
    }
    setConfirming(false);
    void run(api.del(`/admin/tournaments/${tid}/entries/${e.id}`), "Could not remove this team.");
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 text-sm">
      {/* The controls drop under the name when a column is narrow, rather
          than truncating "River Bandits" to make room for them. */}
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <span className="min-w-0 flex-1 basis-28">
          <span className="block truncate font-medium text-navy-950">{name}</span>
          {attrs ? <span className="block truncate text-xs text-slate-500">{attrs}</span> : null}
        </span>
        <span className="flex shrink-0 items-center gap-1.5">
          {pools.length > 0 ? (
            <Select
              uiSize="sm"
              className="w-32"
              aria-label={`${name} pool`}
              value={e.poolId ?? ""}
              onChange={(ev) =>
                void run(
                  api.put(`/admin/tournaments/${tid}/entries/${e.id}/pool`, { poolId: ev.target.value || null }),
                  "Could not move this team.",
                )
              }
            >
              <option value="">Unassigned</option>
              {pools.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </Select>
          ) : null}
          <Button
            size="sm"
            variant={confirming ? "danger" : "ghost"}
            aria-label={confirming ? undefined : `Remove ${name}`}
            onClick={remove}
            onBlur={() => setConfirming(false)}
          >
            {confirming
              ? entryRemoveConfirm({ team: name, fromRegistration: Boolean(e.registrationId), scheduledGames })
              : "×"}
          </Button>
        </span>
      </div>
      {error ? <p className="mt-1 text-xs text-red-600">{error}</p> : null}
    </div>
  );
}

/**
 * Add a team by searching the library first (console spec §5.3): a match is
 * entered as it is, and only a name nobody has creates a team. `quickAddEntry`
 * would happily enter the same team twice, so a match already in the division
 * is offered as text, not as a button.
 */
function AddTeam({ tid, bundle }: { tid: string; bundle: Bundle }) {
  const divisions = orderedDivisions(bundle.divisions);
  const [term, setTerm] = useState("");
  const [divisionId, setDivisionId] = useState(divisions.length === 1 ? divisions[0]!.id : "");
  const [poolId, setPoolId] = useState("");
  const [creating, setCreating] = useState<string | null>(null);
  const [ageGroup, setAgeGroup] = useState("");
  const [classification, setClassification] = useState("");
  const [error, setError] = useState<string>();
  const refresh = useAdminMutation(tid, () => Promise.resolve());

  const debounced = useDebouncedValue(term, 250);
  const searchQ = useTeamSearch(debounced);
  const pools = [...bundle.pools.filter((p) => p.divisionId === divisionId)].sort((a, b) => a.order - b.order);
  const entered = new Set(bundle.entries.filter((e) => e.divisionId === divisionId).map((e) => e.teamId));
  const rows = term.trim().length >= 2 ? searchRows(searchQ.data?.teams ?? [], term, entered) : [];

  const post = (body: Record<string, unknown>) =>
    api
      .post(`/admin/tournaments/${tid}/entries`, { divisionId, poolId: poolId || null, ...body })
      .then(() => {
        setError(undefined);
        setTerm("");
        setCreating(null);
        refresh.mutate();
      })
      .catch((e) => setError(apiErrorMessage(e, "Could not add this team.")));

  return (
    <SectionCard title="Add a team" description="Search the teams already in the system first; create one only when nobody has that name.">
      <div className="flex flex-wrap gap-2">
        <Input
          className="w-full sm:max-w-64"
          placeholder="Team name starts with…"
          value={term}
          onChange={(e) => {
            setTerm(e.target.value);
            setCreating(null);
          }}
          aria-label="Team name"
        />
        <Select className="w-40" value={divisionId} onChange={(e) => { setDivisionId(e.target.value); setPoolId(""); }} aria-label="Division">
          <option value="">Division…</option>
          {divisions.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </Select>
        <Select className="w-36" value={poolId} onChange={(e) => setPoolId(e.target.value)} aria-label="Pool" disabled={pools.length === 0}>
          <option value="">Unassigned</option>
          {pools.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </Select>
      </div>
      {!divisionId ? <p className="text-xs text-slate-500">Pick a division to add to.</p> : null}
      {term.trim().length > 0 && term.trim().length < 2 ? (
        <p className="text-xs text-slate-400">Type at least two letters to search.</p>
      ) : null}
      {rows.length > 0 && divisionId ? (
        <SearchResults
          rows={rows}
          searching={searchQ.isFetching}
          onPick={(teamId) => void post({ teamId })}
          onCreate={(name) => setCreating(name)}
        />
      ) : null}
      {creating ? (
        <form
          className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-slate-50/60 p-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (ageGroup && classification) void post({ newTeam: { name: creating, ageGroup, classification } });
          }}
        >
          <span className="text-sm text-slate-700">
            Create <span className="font-medium text-navy-950">{creating}</span> as
          </span>
          <Select uiSize="sm" className="w-28" value={ageGroup} onChange={(e) => setAgeGroup(e.target.value)} aria-label="Age group">
            <option value="">Age…</option>
            {AGE_GROUPS.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </Select>
          <Select uiSize="sm" className="w-32" value={classification} onChange={(e) => setClassification(e.target.value)} aria-label="Classification">
            <option value="">Class…</option>
            {CLASSIFICATIONS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
          <Button type="submit" size="sm" disabled={!ageGroup || !classification}>
            Add {creating}
          </Button>
        </form>
      ) : null}
      {error ? <p className="text-sm text-red-600">{error}</p> : null}
    </SectionCard>
  );
}

/** The rows under the search box. Rendered directly by the tests, since the search itself never runs there. */
export function SearchResults({
  rows,
  searching,
  onPick,
  onCreate,
}: {
  rows: SearchRow[];
  searching: boolean;
  onPick: (teamId: string) => void;
  onCreate: (name: string) => void;
}) {
  return (
    <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white text-sm">
      {rows.map((row) =>
        row.kind === "team" ? (
          <li key={row.team.id} className="flex items-center justify-between gap-3 px-3 py-2">
            <span className="min-w-0 truncate">
              <span className="font-medium text-navy-950">{row.team.name}</span>
              <span className="ml-2 text-xs text-slate-500">
                {row.team.ageGroup} {row.team.classification}
              </span>
            </span>
            {row.alreadyEntered ? (
              <span className="shrink-0 text-xs text-slate-400">Already entered</span>
            ) : (
              <Button size="sm" variant="secondary" onClick={() => onPick(row.team.id)}>
                Add
              </Button>
            )}
          </li>
        ) : (
          <li key="create" className="flex items-center justify-between gap-3 px-3 py-2">
            <span className="text-slate-600">No team named exactly that yet.</span>
            <Button size="sm" onClick={() => onCreate(row.name)}>
              Create “{row.name}”
            </Button>
          </li>
        ),
      )}
      {searching ? <li className="px-3 py-2 text-xs text-slate-400">Searching…</li> : null}
    </ul>
  );
}

/**
 * The approval queue (spec §8.4). A row leads with the team and division
 * because that is what the decision is about — a coach's name does not say
 * what is being let into the tournament — and carries the contact underneath
 * so an admin can chase a questionable entry without leaving the page.
 *
 * The pool is the coach's own pick, carried onto the entry at approval — a
 * starting point, not a commitment. Reassigning still happens on the entry
 * under Entries above, so there is nothing to decide about it here.
 */
function Registrations({ tid }: { tid: string }) {
  const regsQ = useAdminRegistrations(tid);
  const act = useAdminMutation(tid, () => Promise.resolve());
  const pending = (regsQ.data?.registrations ?? []).filter((r) => r.status === "pending");

  return (
    <SectionCard
      title="Registration queue"
      description="Approving a team creates its entry in the pool it registered for. Change it under Entries."
      badge={
        pending.length > 0 ? (
          <Badge tone="amber" pulse>
            {pending.length} pending
          </Badge>
        ) : undefined
      }
    >
      {regsQ.isLoading ? (
        <p className="text-sm text-slate-400">Loading registrations…</p>
      ) : regsQ.isError ? (
        // Before the empty case, and in red rather than the muted gray the
        // other states use: a failed request has no pending registrations
        // either, and falling through would tell an admin that teams waiting
        // on a decision are all caught up.
        <p className="text-sm text-red-600">
          Couldn&rsquo;t load the registration queue. Check your connection and try again.
        </p>
      ) : pending.length === 0 ? (
        <p className="text-sm text-slate-400">No pending registrations — all caught up.</p>
      ) : (
        // Two across: each row is a decision — team, division, who to call —
        // and pairs of them fit a wide screen without the Approve/Reject
        // buttons drifting half a screen from the team they act on.
        <div className="grid gap-2 xl:grid-cols-2">
          {pending.map((r) => {
            // r.team can exist with no attributes yet (pre-backfill team), and
            // teamAttrs then returns "" — guard on the string, not just on the
            // team existing, or the Badge renders as a visible empty pill.
            const attrs = r.team ? teamAttrs(r.team) : "";
            return (
              <div
                key={r.id}
                className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-slate-200 bg-white p-3 text-sm"
              >
                <div className="min-w-0 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    {/* A team or division deleted while its registration sat here
                        leaves the row standing rather than rendering "undefined". */}
                    <p className="min-w-0 truncate font-medium text-navy-950">
                      {r.team?.name ?? "Team unavailable"}
                    </p>
                    {attrs ? <Badge tone="brand">{attrs}</Badge> : null}
                    <Badge tone="slate">{r.division?.name ?? "Division unavailable"}</Badge>
                    {/* Absent rather than "No pool" when there is none: a
                        division with no pools is the common case, and a badge
                        on every row saying so would drown the rows that do
                        name one. The entry lands unpooled either way. */}
                    {r.pool ? <Badge tone="slate">{r.pool.name}</Badge> : null}
                  </div>
                  <p className="truncate text-slate-600">{r.contactName}</p>
                  <p className="truncate text-xs text-slate-500">
                    {r.contactPhone ? `${r.contactEmail} · ${r.contactPhone}` : r.contactEmail}
                  </p>
                </div>
                <div className="flex shrink-0 gap-2">
                  <Button
                    size="sm"
                    onClick={() =>
                      api
                        .post(`/admin/tournaments/${tid}/registrations/${r.id}/approve`)
                        .then(() => act.mutate())
                    }
                  >
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() =>
                      api
                        .post(`/admin/tournaments/${tid}/registrations/${r.id}/reject`, {})
                        .then(() => act.mutate())
                    }
                  >
                    Reject
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </SectionCard>
  );
}
